import classNames from "components/ui/classNames";
import type { IBrazilianIssuerDividendsQuery } from "lib/queries/__generated__/BrazilianIssuerDividendsQuery.generated";
import { FormattedMessage, useIntl } from "react-intl";
import type { IDividendsCorporateAction, IDividendYieldDetails } from "types/graphqlTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { useMemo, useState } from "react";
import getYear from "date-fns/getYear";
import parseDateYmd from "lib/utils/parseDateYmd";
import AmericanDividendsPaid from "components/company/v3/american/contents/events/dividends/AmericanDividendsPaid";
import AmericanDividendsPaidByYear from "components/company/v3/american/contents/events/dividends/AmericanDividendsPaidByYear";
import type { TDividendsPaidByYear } from "components/company/v3/brazilian/contents/events/dividends/types";
import AmericanDividendsPaidByYearChart from "components/company/v3/american/contents/events/dividends/AmericanDividendsPaidByYearChart";
import AmericanDividendsPaidChart from "components/company/v3/american/contents/events/dividends/AmericanDividendsPaidChart";
import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import { BarChartIcon, TableIcon } from "lucide-react";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import getDividendYield from "components/company/v3/utils/getDividendYield";
import SadFaceIcon from "components/icons/SadFaceIcon";

interface AmericanDividendsContentProps {
  tickerSymbol: string;
  loading: boolean;
  filterDividendsByYear: number | null;
  setFilterDividendsByYear: (year: number | null) => void;
  data: IDividendsCorporateAction["nodes"];
  availableYears: IBrazilianIssuerDividendsQuery["dividendsPaid"]["availableYears"];
  dividendYieldSnapshotEachSymbol: IDividendYieldDetails[];
  setEnabledFullHeight: (enabled: boolean) => void;
}

export default function AmericanDividendsContent({
  tickerSymbol,
  loading,
  filterDividendsByYear,
  setFilterDividendsByYear,
  data,
  availableYears,
  dividendYieldSnapshotEachSymbol,
  setEnabledFullHeight,
}: AmericanDividendsContentProps) {
  const [groupedByYear, setGroupedByYear] = useState(false);
  const [plotType, setPlotType] = useState<"column" | "table">("table");

  const intl = useIntl();

  const dividendsPaid = data;
  const dividendsPaidByYear = useMemo(
    () =>
      dividendsPaid.reduce((dividendsByYear, node) => {
        const year = getYear(parseDateYmd(node.recordDate));
        const candidate = dividendsByYear.get(year);

        if (!candidate) {
          dividendsByYear.set(year, {
            year,
            dividendsPaid: [node],
            amountPaid: node.amountPaid,
          });
        } else {
          dividendsByYear.set(year, {
            year,
            dividendsPaid: [...candidate.dividendsPaid, node],
            amountPaid: candidate.amountPaid + node.amountPaid,
          });
        }

        return dividendsByYear;
      }, new Map<number, TDividendsPaidByYear>()),
    [dividendsPaid],
  );

  if (loading && !data) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (!data && !filterDividendsByYear) {
    return (
      <div className="flex h-full w-full flex-col items-end space-y-4">
        <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="grid w-full grid-cols-2 items-center justify-start gap-4 lg:flex">
            <div className="w-full">
              <div className="flex h-full w-full flex-col items-center justify-center">
                <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
                  <SadFaceIcon />

                  <p>
                    <FormattedMessage defaultMessage="Nenhum dado encontrado." id="waxTCd" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TODO(Andrey): Improve this?
  if (availableYears.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-end space-y-4">
        <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="grid w-full grid-cols-2 items-center justify-start gap-4 lg:flex">
            <div className="w-full">
              <div className="flex h-full w-full flex-col items-center justify-center">
                <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
                  <SadFaceIcon />

                  <p>
                    <FormattedMessage defaultMessage="Essa empresa ainda não pagou dividendos." id="fBTgN+" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-end space-y-4">
      <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="grid w-full grid-cols-2 items-center justify-start gap-4 lg:flex">
          <div className="w-full md:w-36">
            <Select
              value={filterDividendsByYear ? String(filterDividendsByYear) : "ALL"}
              onValueChange={(value) => {
                if (value === "ALL") {
                  return setFilterDividendsByYear(null);
                }

                return setFilterDividendsByYear(parseInt(value, 10));
              }}
            >
              <SelectTrigger
                className={classNames(
                  "font-aeonik",
                  "w-full rounded-lg",
                  "border border-slate-200 bg-white",
                  "text-sm font-medium tracking-wide text-slate-600",
                  "disabled:select-none",
                  "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
                  "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                  "truncate",
                )}
              >
                <SelectValue
                  placeholder={intl.formatMessage({
                    defaultMessage: "Filtre o ano",
                    id: "0HA0Sr",
                  })}
                />
              </SelectTrigger>
              <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
                <SelectItem value="ALL">
                  {filterDividendsByYear === null
                    ? intl.formatMessage({
                        defaultMessage: "Filtre o ano",
                        id: "0HA0Sr",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Remover filtro",
                        id: "J5Id85",
                      })}
                </SelectItem>

                {availableYears.map((year) => (
                  <SelectItem key={`selectBoxYear/${year}`} value={String(year)} className="w-full">
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="button"
            className={classNames(
              "h-10 rounded-lg px-4 py-2 text-sm font-medium text-slate-600",
              "leading-5 tracking-normal",
              "border border-slate-200 bg-slate-50",
              "hover:bg-indigo-50 hover:text-indigo-600",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
              "focus-visible:border-transparent",
              "disabled:cursor-not-allowed",
              {
                "bg-indigo-50 text-indigo-600": groupedByYear,
              },
            )}
            onClick={() => {
              if (groupedByYear === false) {
                setGroupedByYear(true);
                setFilterDividendsByYear(null);
              } else {
                setGroupedByYear(false);
              }
            }}
          >
            <FormattedMessage defaultMessage="Agrupar por ano" id="wE4cTx" />
          </button>

          <div
            className={classNames({
              "col-span-2": groupedByYear,
            })}
          >
            <PlotTypeSelector
              types={[
                {
                  type: "table",
                  label: intl.formatMessage({
                    id: "jZHHzp",
                    defaultMessage: "Tabela",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <TableIcon className="h-5 w-5" />,
                },
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon className="h-5 w-5" />,
                },
              ]}
              value={plotType}
              onChange={(value) => {
                setPlotType(value);
                if (value === "column") {
                  setEnabledFullHeight(true);
                } else {
                  setEnabledFullHeight(false);
                }
              }}
            />
          </div>

          {!groupedByYear && dividendYieldSnapshotEachSymbol.length > 0 && (
            <SimpleTextTooltip
              content={<FormattedMessage defaultMessage="Dividend Yield dos últimos 12 meses" id="z30HiR" />}
            >
              <div
                className={classNames(
                  "flex justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm text-slate-600 lg:hidden",
                  "bg-yellow-600/10 font-bold text-yellow-600",
                )}
              >
                DY {getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol)}%
              </div>
            </SimpleTextTooltip>
          )}
        </div>

        {groupedByYear && (
          <p className="whitespace-nowrap text-xs italic tracking-wider">
            *<FormattedMessage defaultMessage="Valores baseados na data-ex de pagamento dos dividendos." id="fdvV7R" />
          </p>
        )}

        {!groupedByYear && dividendYieldSnapshotEachSymbol.length > 0 && (
          <SimpleTextTooltip
            content={<FormattedMessage defaultMessage="Dividend Yield dos últimos 12 meses" id="z30HiR" />}
          >
            <div
              className={classNames(
                "hidden whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-slate-600 lg:flex",
                "bg-yellow-600/10 font-bold text-yellow-600",
              )}
            >
              DY {getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol)}%
            </div>
          </SimpleTextTooltip>
        )}
      </div>

      {plotType === "table" && groupedByYear && (
        <AmericanDividendsPaidByYear dividendsPaid={Array.from(dividendsPaidByYear.values())} />
      )}

      {plotType === "table" && !groupedByYear && <AmericanDividendsPaid dividendsPaid={dividendsPaid} />}

      {plotType === "column" && groupedByYear && (
        <AmericanDividendsPaidByYearChart dividendsPaid={Array.from(dividendsPaidByYear.values())} />
      )}

      {plotType === "column" && !groupedByYear && <AmericanDividendsPaidChart dividendsPaid={dividendsPaid} />}
    </div>
  );
}
